// Route constants for the application
export const ROUTES = {
  SUPER_ADMIN: "/super-admin",
  SUPER_ADMIN_DASHBOARD: "/super-admin/dashboard",
  SUPER_ADMIN_OPERATOR_LIST: "/super-admin/operator/list",
  SUPER_ADMIN_OPERATOR_BATCHES_LIST: "/super-admin/operator/batches/list",
  SUPER_ADMIN_OPERATOR_BATCH_DETAIL: ({ batchId }: { batchId: string }) =>
    `/super-admin/operator/batches/list/${batchId}`,
  SUPER_ADMIN_OPERATOR_ARCHIVED_LIST: "/super-admin/operator/archived/list",
  // SUPER_ADMIN_KNOWLEDGE_BASES: "/super-admin/knowledge-bases",
  SUPER_ADMIN_STATS: "/super-admin/stats",
  SUPER_ADMIN_STAFF_ACCESS: "/super-admin/staff-access",
  SUPER_ADMIN_SETTINGS: "/super-admin/settings",
  SUPER_ADMIN_SETTINGS_GENERAL: "/super-admin/settings/general",
  SUPER_ADMIN_SETTINGS_USERS: "/super-admin/settings/users",

  // Authentication routes
  LOGIN: "/login",
  LOGOUT: "/logout",
  REGISTER: "/register",
  FORGOT_PASSWORD: "/forgot-password",
  // email ownership confirmation and the self-serve company setup step
  VERIFY_EMAIL: "/verify-email",
  START: "/start",
  RESET_PASSWORD: "/reset-password",

  // I don't think we need this in the long term but we will see
  HOME: "/",
  DOWNLOAD_ENRICHMENT: "/download/enrichment",
  DOWNLOAD_ARCHIVE_REPORT: "/download/archive-report",

  // Organization routes
  ORG_HOME: ({ orgSlug }: { orgSlug: string }) => `/org/${orgSlug}/home`,
  ORG_SETTINGS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/settings`,
  ORG_SETTINGS_GENERAL: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/settings/general`,
  ORG_SETTINGS_MEMBERS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/settings/members`,
  ORG_INSIGHTS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights`,
  ORG_INSIGHTS_FORECAST: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/forecast`,
  ORG_INSIGHTS_YEAR_AGO: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/year-ago`,
  ORG_INSIGHTS_WINS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/wins`,
  ORG_INSIGHTS_LOSSES: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/losses`,
  ORG_INSIGHTS_TEAM: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/team`,
  ORG_INSIGHTS_SALES_ORG: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/sales-org`,
  ORG_INSIGHTS_UPLOAD: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/upload`,
  ORG_INSIGHTS_SWITCH_BRAND: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/switch-brand`,
  ORG_INSIGHTS_SWITCH_REGION: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/switch-region`,
  ORG_INSIGHTS_SWITCH_DISTRIBUTOR: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/insights/switch-distributor`,
  BRAND_TAB: ({ orgSlug }: { orgSlug: string }) => `/org/${orgSlug}/brand/home`,
  OPERATOR_LIST: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/operator/list`,
  BRAND_HOME: ({
    orgSlug,
    brandSlug,
  }: {
    orgSlug: string;
    brandSlug: string;
  }) => `/org/${orgSlug}/brand/${brandSlug}/home`,
  ORG_OPERATOR_DETAILS: ({
    orgSlug,
    operatorSlug,
  }: {
    orgSlug: string;
    operatorSlug: string;
  }) => {
    const pathname = `/org/${orgSlug}/operator/${operatorSlug}`;

    return pathname;
  },
  SUPER_ADMIN_OPERATOR_DETAILS: ({ operatorSlug }: { operatorSlug: string }) =>
    `/super-admin/operator/${operatorSlug}`,
  OPERATOR_MATCH: ({
    orgSlug,
    operatorSlug,
  }: {
    orgSlug: string;
    operatorSlug: string;
  }) => `/org/${orgSlug}/match/${operatorSlug}`,
  OPERATOR_MATCH_LIST: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/match/list`,
  // Leads routes
  LEADS_LIST: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/leads/list`,
  LEAD_DETAILS: ({
    orgSlug,
    operatorSlug,
  }: {
    orgSlug: string;
    operatorSlug: string;
  }) => `/org/${orgSlug}/leads/${operatorSlug}`,
  OPERATORS_ARCHIVED: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/operators/archived`,
  LEAD_UPLOADS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/leads/uploads`,
} as const;

// API routes object - Updated to reflect new organization-based structure
export const API_ROUTES = {
  // Organization routes
  ORG_ROOT: () => `/api/org`,
  ORG_DETAILS: ({ orgId }: { orgId: string }) => `/api/org/${orgId}`,
  // Brand routes (within organization context)
  BRAND_CREATE: ({ orgId }: { orgId: string }) => `/api/org/${orgId}/brands`,
  BRAND_DETAILS: ({ orgId, brandId }: { orgId: string; brandId: string }) =>
    `/api/org/${orgId}/brands/${brandId}`,
  BRAND_UPLOAD_OPERATORS: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/upload-matches`,
  BRAND_UPLOAD_LEADS: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/upload-leads`,
  BRAND_GENERATE_MATCHES: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/generate-matches`,
  BRAND_MATCH_JOB_STATUS: ({
    orgId,
    brandId,
    jobId,
  }: {
    orgId: string;
    brandId: string;
    jobId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/match-job/${jobId}`,
  BRAND_MATCH_JOB_CANCEL: ({
    orgId,
    brandId,
    jobId,
  }: {
    orgId: string;
    brandId: string;
    jobId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/match-job/${jobId}`,
  BRAND_MATCH_LIMIT_STATUS: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/match-limit`,

  // Product routes (within brand context)
  PRODUCTS_BY_BRAND: ({ orgId, brandId }: { orgId: string; brandId: string }) =>
    `/api/org/${orgId}/brands/${brandId}/products`,
  PRODUCT_DETAILS: ({
    orgId,
    brandId,
    productId,
  }: {
    orgId: string;
    brandId: string;
    productId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/products/${productId}`,
  PRODUCT_GROUPS_BY_BRAND: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/product-groups`,
  PRODUCT_GROUP_DETAILS: ({
    orgId,
    brandId,
    productGroupId,
  }: {
    orgId: string;
    brandId: string;
    productGroupId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/product-groups/${productGroupId}`,

  // Signal routes (within brand context)
  BRAND_GENERATE_SIGNALS: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/signals/generate`,
  BRAND_SIGNALS_LIST: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/signals`,
  BRAND_SIGNAL_DETAILS: ({
    orgId,
    brandId,
    signalId,
  }: {
    orgId: string;
    brandId: string;
    signalId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/signals/${signalId}`,

  // Menu Price Point routes (within brand context)
  BRAND_MENU_PRICE_POINTS_LIST: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/menu-price-points`,
  BRAND_MENU_PRICE_POINT_DETAILS: ({
    orgId,
    brandId,
    pricePointId,
  }: {
    orgId: string;
    brandId: string;
    pricePointId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/menu-price-points/${pricePointId}`,

  // Assessment routes (within brand context)
  BRAND_GENERATE_ASSESSMENT: ({
    orgId,
    brandId,
    operatorId,
  }: {
    orgId: string;
    brandId: string;
    operatorId: string;
  }) =>
    `/api/org/${orgId}/brands/${brandId}/assessments/${operatorId}/generate`,

  // Match routes (within organization context)
  MATCH_MARK_SEEN: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/mark-seen`,
  MATCH_HIDE: ({ orgId, operatorId }: { orgId: string; operatorId: string }) =>
    `/api/org/${orgId}/matches/${operatorId}/hide`,
  MATCH_DELETE_OPERATOR: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/delete`,
  OPERATOR_EMAIL_CONFIGURATION: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/email-configuration`,

  // Operator routes (top-level, not organization-specific)
  OPERATOR_LIST: () => `/api/operators`,
  OPERATOR_CREATE: () => `/api/operators`,
  OPERATOR_DETAILS: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}`,
  OPERATOR_BULK_SCRAPE: () => `/api/operators/bulk-scrape`,
  OPERATOR_BULK_DELETE: () => `/api/operators/bulk-delete`,
  OPERATOR_LOCATION_SEARCH: () => `/api/operators/search-location`,
  OPERATOR_UPLOAD: () => `/api/operators/upload`,
  OPERATOR_TIMING_SCORE_RUNS: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/timing-score-runs`,
  OPERATOR_TIMING_SCORE_RUN_LATEST: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/timing-score-runs/latest`,

  // Operator public info routes (within operator context)
  OPERATOR_PUBLIC_INFO_LIST: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/public-info`,
  OPERATOR_PUBLIC_INFO_DETAILS: ({
    operatorId,
    infoId,
  }: {
    operatorId: string;
    infoId: string;
  }) => `/api/operators/${operatorId}/public-info/${infoId}`,
  OPERATOR_PUBLIC_INFO_SCRAPE: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/public-info/scrape`,

  // Operator proprietary info routes (within operator context)
  OPERATOR_PROPRIETARY_INFO_LIST: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/proprietary-info`,
  OPERATOR_PROPRIETARY_INFO_DETAILS: ({
    operatorId,
    infoId,
  }: {
    operatorId: string;
    infoId: string;
  }) => `/api/operators/${operatorId}/proprietary-info/${infoId}`,

  // OCR routes (top-level, not organization-specific)
  OCR_UPLOAD: () => "/api/ocr/upload",

  // Knowledge Base routes (super admin)
  KNOWLEDGE_BASES: () => "/api/knowledge-bases",
  KNOWLEDGE_BASE_DETAILS: ({ kbId }: { kbId: string }) =>
    `/api/knowledge-bases/${kbId}`,
  KNOWLEDGE_BASE_ARTICLES: ({ kbId }: { kbId: string }) =>
    `/api/knowledge-bases/${kbId}/articles`,
  KNOWLEDGE_BASE_ARTICLE_DETAILS: ({
    kbId,
    articleId,
  }: {
    kbId: string;
    articleId: string;
  }) => `/api/knowledge-bases/${kbId}/articles/${articleId}`,
  KNOWLEDGE_BASE_SEARCH: ({ kbId }: { kbId: string }) =>
    `/api/knowledge-bases/${kbId}/search`,
  KNOWLEDGE_BASE_CHAT: ({ kbId }: { kbId: string }) =>
    `/api/knowledge-bases/${kbId}/chat`,

  // Organization additional info routes
  ORG_ADDITIONAL_INFO_DETAILS: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/additional-info`,

  // Platform entitlements (services the org is subscribed/granted)
  ORG_ENTITLEMENTS: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/entitlements`,

  // Operator additional info routes
  OPERATOR_ADDITIONAL_INFO_DETAILS: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/additional-info`,

  // Operator match routes (within organization and brand context)
  OPERATOR_MATCH_DETAILS: ({
    orgId,
    brandId,
    operatorId,
  }: {
    orgId: string;
    brandId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/operators/${operatorId}/match`,
  OPERATOR_MATCH_CREATE: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/matches`,
  OPERATOR_MATCHES_LIST: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/matches`,
  OPERATOR_MATCHES_SEARCH: ({
    orgId,
    query,
  }: {
    orgId: string;
    query: string;
  }) => `/api/org/${orgId}/matches/search?q=${encodeURIComponent(query)}`,
  OPERATOR_MATCHES_BY_BRAND: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/matches`,
  OPERATOR_MATCHES_BY_OPERATOR: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/operators/${operatorId}/matches`,
  OPERATOR_MATCH_EMAIL_DRAFT: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/email-draft`,
  OPERATOR_MATCH_MARK_SEEN: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/mark-seen`,
  OPERATOR_MATCH_HIDE: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/hide`,
  OPERATOR_MATCH_DELETE: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/delete`,
  OPERATOR_MATCH_SCORE_RUNS: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/score-runs`,
  OPERATOR_MATCH_SCORE_RUN_LATEST: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/score-runs/latest`,
  OPERATOR_MATCH_SCORE_FEEDBACK: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/score-runs/feedback`,
  OPERATOR_MATCH_COMPOSITE_SCORE_RUNS: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/composite-score-runs`,
  OPERATOR_MATCH_TIMING_SCORE_RUNS: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/timing-score-runs`,
  OPERATOR_MATCH_TIMING_SCORE_RUN_LATEST: ({
    orgId,
    operatorId,
  }: {
    orgId: string;
    operatorId: string;
  }) => `/api/org/${orgId}/matches/${operatorId}/timing-score-runs/latest`,
  OPERATOR_MATCH_EXPORT: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/matches/export`,
  OPERATOR_MATCH_BATCH_SCORE: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/matches/batch-score`,
  OPERATOR_MATCH_BATCH_TIMING_SCORE: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/matches/batch-timing-score`,
  OPERATOR_MATCH_BULK_CREATE: ({
    orgId,
    brandId,
  }: {
    orgId: string;
    brandId: string;
  }) => `/api/org/${orgId}/brands/${brandId}/matches/bulk`,

  // Menu routes
  MENU_RESEARCH: () => `/api/menus/research`,
  MENU_PROCESS_IMAGES: () => `/api/menus/process-images`,

  // Menu processing status route
  OPERATOR_MENU_PROCESSING_STATUS: ({ operatorId }: { operatorId: string }) =>
    `/api/operators/${operatorId}/menu-processing-status`,

  // Organization Invitations
  ORG_INVITATIONS: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/invitations`,
  ORG_INVITATION_ACCEPT: ({
    orgId,
    invitationId,
  }: {
    orgId: string;
    invitationId: string;
  }) => `/api/org/${orgId}/accept-invitation/${invitationId}`,
  // ORG_INVITATION: ({ orgId, invitationId }: { orgId: string; invitationId: string }) =>
  //   `/api/org/${orgId}/invitations/${invitationId}`,
  ORG_INVITATION_RESEND: ({
    orgId,
    invitationId,
  }: {
    orgId: string;
    invitationId: string;
  }) => `/api/org/${orgId}/invitations/${invitationId}/resend`,

  // Organization Members
  ORG_MEMBER_REMOVE: ({
    orgId,
    memberId,
  }: {
    orgId: string;
    memberId: string;
  }) => `/api/org/${orgId}/members/${memberId}`,
  ORG_MEMBER_UPDATE_ROLE: ({
    orgId,
    memberId,
  }: {
    orgId: string;
    memberId: string;
  }) => `/api/org/${orgId}/members/${memberId}/role`,
  ORG_MEMBER_TRUSTED_PRIVILEGES: ({
    orgId,
    memberId,
  }: {
    orgId: string;
    memberId: string;
  }) => `/api/org/${orgId}/members/${memberId}/trusted-privileges`,

  // Authentication routes
  AUTH_REQUEST_PASSWORD_RESET: () => "/api/auth/request-password-reset",
  AUTH_RESET_PASSWORD: () => "/api/auth/reset-password",

  // Analytics (protected)
  ANALYTICS_TRACK: () => "/api/analytics/track",

  // Super Admin routes
  SUPER_ADMIN_INVITATIONS: () => `/api/super-admin/invitations`,
  SUPER_ADMIN_INVITATION_ACCEPT: (invitationId: string) =>
    `/api/super-admin/invitations/${invitationId}/accept`,
  SUPER_ADMIN_USER_REMOVE: ({ userId }: { userId: string }) =>
    `/api/super-admin/users/${userId}`,
  SUPER_ADMIN_RE_ENRICH_OPERATOR: () => `/api/super-admin/enrichment/re-enrich`,
  SUPER_ADMIN_BATCH_RETRY: () => `/api/super-admin/enrichment/batch-retry`,
  SUPER_ADMIN_BATCHES_LIST: () => `/api/super-admin/enrichment/batches`,

  // Lead upload batch routes
  LEAD_UPLOADS_LIST: ({ orgId }: { orgId: string }) =>
    `/api/org/${orgId}/lead-uploads`,
  LEAD_UPLOADS_ERRORS: ({
    orgId,
    batchId,
  }: {
    orgId: string;
    batchId: string;
  }) => `/api/org/${orgId}/lead-uploads/${batchId}/errors`,
  LEAD_UPLOADS_DOWNLOAD: ({
    orgId,
    batchId,
  }: {
    orgId: string;
    batchId: string;
  }) => `/api/org/${orgId}/lead-uploads/${batchId}/download`,

  // Location routes
  LOCATION_SEARCH: () => `/api/location/search`,
} as const;

// Type for route keys
export type RouteKey = keyof typeof ROUTES;
export type ApiRouteKey = keyof typeof API_ROUTES;

// Helper function to get route by key
export function getRoute(key: RouteKey): string {
  return ROUTES[key] as string;
}

// Helper function to get API route by key
export function getApiRoute(
  key: ApiRouteKey,
  params?: Record<string, string>
): string {
  const route = API_ROUTES[key];
  if (typeof route === "function") {
    return route(params as any);
  }
  return route;
}
